import { isNil } from "lodash";
import { AiccPromptConfig } from "types/model/aiccPrompt";
import { normalisePromptMarkdown } from "../PromptEditor/markdown";

export const PROMPT_DIFF_KIND = {
  unchanged: "unchanged",
  added: "added",
  removed: "removed",
} as const;

export type PromptDiffKind = typeof PROMPT_DIFF_KIND[keyof typeof PROMPT_DIFF_KIND];

export interface PromptDiffLine {
  kind: PromptDiffKind;
  text: string;
}

export interface PromptTextDiff {
  lines: PromptDiffLine[];
  added: number;
  removed: number;
  isIdentical: boolean;
  isTruncated: boolean;
}

// 🔴 The LCS is O(n × m) on every keystroke; past this the editor stutters while typing.
export const PROMPT_DIFF_LINE_LIMIT = 400;

const EMPTY_DIFF: PromptTextDiff = { lines: [], added: 0, removed: 0, isIdentical: true, isTruncated: false };

const wholeSides = (reference: string[], draft: string[]): PromptTextDiff => ({
  lines: [
    ...reference.map((text) => ({ kind: PROMPT_DIFF_KIND.removed, text })),
    ...draft.map((text) => ({ kind: PROMPT_DIFF_KIND.added, text })),
  ],
  added: draft.length,
  removed: reference.length,
  isIdentical: false,
  isTruncated: true,
});

function lcsLengths(reference: string[], draft: string[]): number[][] {
  const table: number[][] = Array.from({ length: reference.length + 1 }, () => new Array(draft.length + 1).fill(0));

  for (let i = reference.length - 1; i >= 0; i -= 1) {
    for (let j = draft.length - 1; j >= 0; j -= 1) {
      table[i][j] = reference[i] === draft[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  return table;
}

// 🔴 Normalised: the editor's round trip turns one newline into a blank line, so raw text differs as soon as anyone clicks in.
export const promptTextMatches = (left: string, right: string) =>
  normalisePromptMarkdown(left) === normalisePromptMarkdown(right);

// 🔴 Computed here, not `GET /flow-versions/diff`: that compares saved copies, and this is about unsaved typing.
export function promptTextDiff(rawReference: string, rawDraft: string): PromptTextDiff {
  const reference = normalisePromptMarkdown(rawReference);
  const draft = normalisePromptMarkdown(rawDraft);

  if (reference === draft) {
    return EMPTY_DIFF;
  }

  const referenceLines = reference.split("\n");
  const draftLines = draft.split("\n");

  if (referenceLines.length > PROMPT_DIFF_LINE_LIMIT || draftLines.length > PROMPT_DIFF_LINE_LIMIT) {
    return wholeSides(referenceLines, draftLines);
  }

  const table = lcsLengths(referenceLines, draftLines);
  const lines: PromptDiffLine[] = [];
  let added = 0;
  let removed = 0;
  let i = 0;
  let j = 0;

  while (i < referenceLines.length && j < draftLines.length) {
    if (referenceLines[i] === draftLines[j]) {
      lines.push({ kind: PROMPT_DIFF_KIND.unchanged, text: referenceLines[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      lines.push({ kind: PROMPT_DIFF_KIND.removed, text: referenceLines[i] });
      removed += 1;
      i += 1;
    } else {
      lines.push({ kind: PROMPT_DIFF_KIND.added, text: draftLines[j] });
      added += 1;
      j += 1;
    }
  }

  for (; i < referenceLines.length; i += 1) {
    lines.push({ kind: PROMPT_DIFF_KIND.removed, text: referenceLines[i] });
    removed += 1;
  }
  for (; j < draftLines.length; j += 1) {
    lines.push({ kind: PROMPT_DIFF_KIND.added, text: draftLines[j] });
    added += 1;
  }

  return { lines, added, removed, isIdentical: false, isTruncated: false };
}

export interface PromptSettingChange {
  label: string;
  from: string;
  to: string;
}

const stated = (value: string | undefined, fallback: string) => value || fallback;

export function promptSettingChanges(
  reference: AiccPromptConfig,
  draft: AiccPromptConfig,
  options: { modelName: (id?: string) => string | undefined; inherited: string }
): PromptSettingChange[] {
  const changes: PromptSettingChange[] = [];

  if (reference.modelId !== draft.modelId) {
    changes.push({
      label: "Model",
      from: stated(options.modelName(reference.modelId) ?? reference.modelId, options.inherited),
      to: stated(options.modelName(draft.modelId) ?? draft.modelId, options.inherited),
    });
  }
  if (reference.temperature !== draft.temperature) {
    changes.push({
      label: "Temperature",
      from: isNil(reference.temperature) ? options.inherited : reference.temperature.toFixed(1),
      to: isNil(draft.temperature) ? options.inherited : draft.temperature.toFixed(1),
    });
  }
  if (reference.reasoningEffort !== draft.reasoningEffort) {
    changes.push({
      label: "Reasoning effort",
      from: stated(reference.reasoningEffort, options.inherited),
      to: stated(draft.reasoningEffort, options.inherited),
    });
  }
  return changes;
}
